import React, { InputHTMLAttributes, useState } from 'react';
import clsx from 'clsx';

import usePasswordFieldText from './hooks/usePasswordFieldMessage';

import { PasswordFieldIcon } from './PasswordFieldIcon/PasswordFieldIcon';

import styles from './PasswordField.module.scss';

export interface IPasswordFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * TextField errorMessage
   */
  errorMessage?: string;
  /**
   * TextField correctMessage
   */
  correctMessage?: string;
  /**
   * Label for the input
   */
  label?: string;
  /**
   * TextField id
   */
  id: string;
}

export function PasswordField({
  errorMessage = '',
  correctMessage = '',
  className = '',
  autoComplete = 'off',
  label = '',
  disabled = false,
  id,
  onChange,
  ...rest
}: IPasswordFieldProps) {
  const [inputType, setInputType] = useState('password')
  const [inputText, setInputText] = useState('');
  const hasError = !!errorMessage;
  const isCorrect = !!correctMessage && !hasError;

  const message = usePasswordFieldText({ correctMessage, errorMessage });

  const onIconClick = () => {
    setInputType(inputType === 'password' ? 'text' : 'password')
  }

  const containerClass = clsx(styles['text-field-container'], className)

  const containerInputClass = clsx(styles['container-input'], {
    [styles['container-input--error']]: hasError,
    disabled,
  });

  const labelClass = clsx(styles['text-field__label'], {
    [styles['text-field__label--error']]: hasError,
  });

  const messageClass = clsx({
    [styles['text-field__message--error']]: hasError,
    [styles['text-field__message--correct']]: isCorrect,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);

    if (onChange != null) {
      onChange(event);
    }
  };

  return (
    <div className={containerClass}>
      {!!label && (
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
      )}
      <div className={containerInputClass} aria-hidden>
        <input
          id={id}
          autoComplete={autoComplete}
          onChange={handleChange}
          value={inputText}
          type={inputType}
          disabled={disabled}
          {...rest}
        />
        <PasswordFieldIcon show={inputType === 'password'} onClick={onIconClick} />
      </div>
      {!!message && <span className={messageClass}>{message}</span>}
    </div>
  );
}
